import { Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { listBankAccounts, listTransactions } from "@/features/banking/api";
import { AddAccountDialog } from "@/features/banking/components/add-account-dialog";
import { AddTransactionDialog } from "@/features/banking/components/add-transaction-dialog";
import {
  BANK_ACCOUNT_TYPE_LABEL,
  type BankAccount,
  type BankTransaction,
} from "@/features/banking/types";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatShortDate } from "@/lib/format";

function errText(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

/** True when a signed decimal string is negative (a debit). String-only —
 * never parsed into a float, to keep money exact. */
function isDebit(amount: string): boolean {
  return amount.trim().startsWith("-");
}

/** "+$42.50" / "-$42.50" from a signed decimal string, without float math. */
function formatSignedAmount(amount: string): string {
  const trimmed = amount.trim();
  const negative = trimmed.startsWith("-");
  const digits = trimmed.replace(/^[+-]/, "");
  return `${negative ? "-" : "+"}$${digits}`;
}

const ACCOUNTS_COLS = 5;
const TXN_COLS = 4;

export function BankingPanel({ clientId }: { clientId: string }) {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addAccountOpen, setAddAccountOpen] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnError, setTxnError] = useState<string | null>(null);
  const [addTxnOpen, setAddTxnOpen] = useState(false);

  const mountedRef = useRef(true);
  const accountsReqRef = useRef(0);
  const txnReqRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadAccounts = useCallback(() => {
    const reqId = ++accountsReqRef.current;
    setLoading(true);
    setError(null);
    listBankAccounts(clientId)
      .then((rows) => {
        if (!mountedRef.current || accountsReqRef.current !== reqId) return;
        setAccounts(rows);
      })
      .catch((err) => {
        if (!mountedRef.current || accountsReqRef.current !== reqId) return;
        setAccounts([]);
        setError(errText(err, "Failed to load bank accounts."));
      })
      .finally(() => {
        if (mountedRef.current && accountsReqRef.current === reqId) setLoading(false);
      });
  }, [clientId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const loadTransactions = useCallback(
    (accountId: string) => {
      const reqId = ++txnReqRef.current;
      setTxnLoading(true);
      setTxnError(null);
      listTransactions(clientId, accountId)
        .then((rows) => {
          if (!mountedRef.current || txnReqRef.current !== reqId) return;
          setTransactions(rows);
        })
        .catch((err) => {
          if (!mountedRef.current || txnReqRef.current !== reqId) return;
          setTransactions([]);
          setTxnError(errText(err, "Failed to load transactions."));
        })
        .finally(() => {
          if (mountedRef.current && txnReqRef.current === reqId) setTxnLoading(false);
        });
    },
    [clientId],
  );

  useEffect(() => {
    if (selectedId) loadTransactions(selectedId);
  }, [selectedId, loadTransactions]);

  const selectedAccount = accounts.find((a) => a.id === selectedId) ?? null;

  function handleAccountCreated(account: BankAccount) {
    setAccounts((prev) => [...prev, account]);
    setSelectedId(account.id);
  }

  function handleTransactionCreated(txn: BankTransaction) {
    setTransactions((prev) => [txn, ...prev]);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-foreground mb-2 text-3xl font-bold">Banking</h1>
          <p className="text-muted-foreground max-w-2xl">
            Capture this client&apos;s bank accounts and transactions for bookkeeping and
            reconciliation.
          </p>
        </div>
        <Button size="sm" onClick={() => setAddAccountOpen(true)}>
          <Plus /> Add account
        </Button>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Account</TableHead>
              <TableHead>Institution</TableHead>
              <TableHead>Number</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Transactions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={ACCOUNTS_COLS} className="py-10">
                  <Loader />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={ACCOUNTS_COLS} className="text-destructive text-center">
                  {error}
                </TableCell>
              </TableRow>
            ) : accounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={ACCOUNTS_COLS} className="text-muted-foreground text-center">
                  No bank accounts yet. Add one to get started.
                </TableCell>
              </TableRow>
            ) : (
              accounts.map((account) => (
                <TableRow
                  key={account.id}
                  data-state={account.id === selectedId ? "selected" : undefined}
                  className="cursor-pointer"
                  role="button"
                  tabIndex={0}
                  aria-label={`View transactions for ${account.name}`}
                  onClick={() => setSelectedId(account.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedId(account.id);
                    }
                  }}
                >
                  <TableCell className="text-foreground font-medium">{account.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {account.institution || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {account.accountLast4 ? `••${account.accountLast4}` : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {BANK_ACCOUNT_TYPE_LABEL[account.accountType]}
                  </TableCell>
                  <TableCell className="text-primary text-right text-sm font-medium">
                    View
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {selectedAccount && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-foreground text-xl font-bold">
              Transactions — {selectedAccount.name}
            </h2>
            <Button size="sm" variant="outline" onClick={() => setAddTxnOpen(true)}>
              <Plus /> Add transaction
            </Button>
          </div>

          <Card className="gap-0 overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txnLoading ? (
                  <TableRow>
                    <TableCell colSpan={TXN_COLS} className="py-10">
                      <Loader />
                    </TableCell>
                  </TableRow>
                ) : txnError ? (
                  <TableRow>
                    <TableCell colSpan={TXN_COLS} className="text-destructive text-center">
                      {txnError}
                    </TableCell>
                  </TableRow>
                ) : transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={TXN_COLS} className="text-muted-foreground text-center">
                      No transactions yet for this account.
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell className="text-muted-foreground">
                        {formatShortDate(txn.date)}
                      </TableCell>
                      <TableCell className="text-foreground font-medium">
                        {txn.description}
                        {txn.category && (
                          <span className="text-muted-foreground block text-xs font-normal">
                            {txn.category}
                          </span>
                        )}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-semibold tabular-nums",
                          isDebit(txn.amount) ? "text-destructive" : "text-success",
                        )}
                      >
                        {formatSignedAmount(txn.amount)}
                      </TableCell>
                      <TableCell>
                        {txn.reconciled ? (
                          <Badge variant="default">Reconciled</Badge>
                        ) : (
                          <Badge variant="outline">Unreconciled</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      <AddAccountDialog
        open={addAccountOpen}
        onOpenChange={setAddAccountOpen}
        clientId={clientId}
        onCreated={handleAccountCreated}
      />

      {selectedAccount && (
        <AddTransactionDialog
          open={addTxnOpen}
          onOpenChange={setAddTxnOpen}
          clientId={clientId}
          accountId={selectedAccount.id}
          onCreated={handleTransactionCreated}
        />
      )}
    </div>
  );
}
